import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { AlertTriangle, ShieldAlert, HelpCircle } from 'lucide-react'
import { useOverlay } from './overlays.js'
import { usePresence } from './presence.js'

/**
 * ConfirmActionDialog (V2.1 §75-87) — LA primitive de confirmation du Design
 * System. Remplace DÉFINITIVEMENT window.confirm : plus jamais de popup
 * navigateur « gestion.liveclubrennes.com indique… ».
 *
 * Variantes :
 *  · standard  — action réversible (désassigner un badge) ;
 *  · sensitive — conséquence métier (désactiver un utilisateur) ;
 *  · critical  — conséquence PHYSIQUE ou irréversible (déverrouiller une
 *    porte, urgence, suppression) — accent rouge, verbe explicite.
 *
 * Usage : const confirm = useConfirm()
 *         if (!(await confirm({ title, message, variant: 'critical' }))) return
 * Le step-up éventuel reste géré par adminMutate/runCommand APRÈS le oui.
 */

export type ConfirmVariant = 'standard' | 'sensitive' | 'critical'

export interface ConfirmOptions {
  title: string
  /** Phrase principale — dit la CONSÉQUENCE, pas le moyen technique. */
  message: string
  /** Lignes de contexte (ex. porte, règle active, verrou, position). */
  details?: { label: string; value: string }[]
  variant?: ConfirmVariant
  confirmLabel?: string
  cancelLabel?: string
}

type Resolver = (ok: boolean) => void
const ConfirmCtx = createContext<((opts: ConfirmOptions) => Promise<boolean>) | null>(null)

export function useConfirm(): (opts: ConfirmOptions) => Promise<boolean> {
  const fn = useContext(ConfirmCtx)
  // Hors provider (ne doit pas arriver) : refuser plutôt qu'une popup native.
  return fn ?? (async () => false)
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [current, setCurrent] = useState<ConfirmOptions | null>(null)
  const resolver = useRef<Resolver | null>(null)

  const ask = useCallback((opts: ConfirmOptions): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      // Une seule confirmation à la fois : la précédente est refusée.
      resolver.current?.(false)
      resolver.current = resolve
      setCurrent(opts)
    })
  }, [])

  const close = useCallback((ok: boolean) => {
    resolver.current?.(ok)
    resolver.current = null
    setCurrent(null)
  }, [])

  return (
    <ConfirmCtx.Provider value={ask}>
      {children}
      <ConfirmDialog opts={current} onClose={close} />
    </ConfirmCtx.Provider>
  )
}

function ConfirmDialog({ opts, onClose }: { opts: ConfirmOptions | null; onClose: (ok: boolean) => void }) {
  const open = opts !== null
  useOverlay(open, () => onClose(false), { lockScroll: true })
  const presence = usePresence(open, 200)
  const keep = useRef<ConfirmOptions | null>(null)
  if (opts) keep.current = opts
  const o = opts ?? keep.current
  if (!presence.mounted || !o) return null
  const variant = o.variant ?? 'standard'
  const Icon = variant === 'critical' ? ShieldAlert : variant === 'sensitive' ? AlertTriangle : HelpCircle
  return createPortal(
    <div className="lc-modal-overlay lc-confirm-overlay" data-state={presence.state} role="presentation" onClick={() => onClose(false)}>
      <div
        className={`lc-modal lc-confirm lc-confirm--${variant}`}
        data-state={presence.state}
        role="dialog" aria-modal="true" aria-label={o.title}
        onClick={(e) => e.stopPropagation()}
      >
        <span className="lc-modal__fade" aria-hidden="true" />
        <div className="lc-confirm__body">
          <span className={`lc-confirm__icon lc-confirm__icon--${variant}`} aria-hidden="true"><Icon size={20} /></span>
          <div className="lc-confirm__text">
            <h2 className="lc-confirm__title">{o.title}</h2>
            <p className="lc-confirm__message">{o.message}</p>
            {o.details && o.details.length > 0 ? (
              <dl className="lc-confirm__details">
                {o.details.map((d, i) => <div key={i}><dt>{d.label}</dt><dd>{d.value}</dd></div>)}
              </dl>
            ) : null}
          </div>
        </div>
        <div className="lc-confirm__actions">
          <button type="button" className="lc-btn lc-btn--ghost" onClick={() => onClose(false)} autoFocus={variant === 'critical'}>
            {o.cancelLabel ?? 'Annuler'}
          </button>
          <button
            type="button"
            className={`lc-btn ${variant === 'critical' ? 'lc-btn--danger' : 'lc-btn--primary'}`}
            onClick={() => onClose(true)}
            autoFocus={variant !== 'critical'}
          >
            {o.confirmLabel ?? 'Confirmer'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
